import React, { forwardRef } from 'react';
import styles from './Card.module.css';

export type CardVariant = 'default' | 'outlined' | 'flat';
export type CardPadding = 'none' | 'sm' | 'md' | 'lg';

export interface CardProps extends React.ComponentPropsWithoutRef<'div'> {
  variant?: CardVariant;
  padding?: CardPadding;
  interactive?: boolean;
  fullWidth?: boolean;
}

export interface CardHeaderProps extends Omit<React.ComponentPropsWithoutRef<'div'>, 'title'> {
  title?: React.ReactNode;
  subtitle?: React.ReactNode;
  action?: React.ReactNode;
}

export type CardBodyProps   = React.ComponentPropsWithoutRef<'div'>;
export type CardFooterProps = React.ComponentPropsWithoutRef<'div'>;

const cx = (...classes: (string | false | null | undefined)[]) =>
  classes.filter(Boolean).join(' ');

/* ── Card ── */

const CardRoot = forwardRef<HTMLDivElement, CardProps>(
  ({ variant = 'default', padding = 'md', interactive, fullWidth, className, children, ...rest }, ref) => (
    <div
      ref={ref}
      className={cx(
        styles.card,
        styles[variant],
        padding !== 'none' && styles[`padding-${padding}`],
        interactive && styles.interactive,
        fullWidth && styles.fullWidth,
        className,
      )}
      {...rest}
    >
      {children}
    </div>
  ),
);

CardRoot.displayName = 'Card';

/* ── Card.Header ── */

const CardHeader = forwardRef<HTMLDivElement, CardHeaderProps>(
  ({ title, subtitle, action, className, children, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.header, className)} {...rest}>
      {(title != null || subtitle != null) && (
        <div className={styles.headerText}>
          {title != null && <span className={styles.title}>{title}</span>}
          {subtitle != null && <span className={styles.subtitle}>{subtitle}</span>}
        </div>
      )}
      {children}
      {action}
    </div>
  ),
);

CardHeader.displayName = 'Card.Header';

/* ── Card.Body ── */

const CardBody = forwardRef<HTMLDivElement, CardBodyProps>(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.body, className)} {...rest}>{children}</div>
  ),
);

CardBody.displayName = 'Card.Body';

/* ── Card.Footer ── */

const CardFooter = forwardRef<HTMLDivElement, CardFooterProps>(
  ({ className, children, ...rest }, ref) => (
    <div ref={ref} className={cx(styles.footer, className)} {...rest}>{children}</div>
  ),
);

CardFooter.displayName = 'Card.Footer';

/* ── Compound export ── */

export const Card = Object.assign(CardRoot, {
  Header: CardHeader,
  Body: CardBody,
  Footer: CardFooter,
});
